import { Badge, type BadgeProps } from "@atlas/core";
import { IHubCatalogStatus, type IHubCatalogVisibility } from "@enums";
import type { ReactElement } from "react";

type HubCatalogLifecycleBadgeVariant =
  | "neutral"
  | "info"
  | "destructive"
  | "success"
  | "warning"
  | "intelligence";

interface IHubCatalogLifecycleBadge {
  id: string;
  label: string;
  variant: HubCatalogLifecycleBadgeVariant;
  appearance: BadgeProps["appearance"];
  size: BadgeProps["size"];
}

type HubCatalogLifecycleTranslate = (
  key: string,
  options?: { defaultValue?: string },
) => string;

export function resolveHubCatalogLifecycleBadge(
  status: IHubCatalogStatus,
  t: HubCatalogLifecycleTranslate,
  _visibility?: IHubCatalogVisibility,
): IHubCatalogLifecycleBadge | null {
  switch (status) {
    case IHubCatalogStatus.Draft:
      return {
        id: "status-draft",
        label: t("hub.catalog_manager.catalog_status_draft", {
          defaultValue: "Draft",
        }),
        variant: "warning",
        appearance: "light",
        size: "sm",
      };
    case IHubCatalogStatus.Submitted:
      return {
        id: "status-submitted",
        label: t("hub.catalog_manager.catalog_status_submitted", {
          defaultValue: "Submitted",
        }),
        variant: "info",
        appearance: "light",
        size: "sm",
      };
    case IHubCatalogStatus.Review:
      return {
        id: "status-review",
        label: t("hub.catalog_manager.catalog_status_review", {
          defaultValue: "Review",
        }),
        variant: "info",
        appearance: "light",
        size: "sm",
      };
    case IHubCatalogStatus.Rejected:
      return {
        id: "status-rejected",
        label: t("hub.catalog_manager.reviews_status_rejected_badge", {
          defaultValue: "Rejected",
        }),
        variant: "destructive",
        appearance: "light",
        size: "sm",
      };
    case IHubCatalogStatus.Approved:
      return {
        id: "status-approved",
        label: t("hub.catalog_manager.catalog_status_approved", {
          defaultValue: "Approved",
        }),
        variant: "success",
        appearance: "light",
        size: "sm",
      };
    default:
      return null;
  }
}

type HubCatalogLifecycleBadgeElementProps = Omit<
  BadgeProps,
  "children" | "variant"
>;

export function getHubCatalogLifecycleBadge(
  status: IHubCatalogStatus,
  t: HubCatalogLifecycleTranslate,
  badgeProps?: HubCatalogLifecycleBadgeElementProps,
  visibility?: IHubCatalogVisibility,
): ReactElement | null {
  const meta = resolveHubCatalogLifecycleBadge(status, t, visibility);
  if (!meta) return null;
  return (
    <Badge
      size={meta.size}
      appearance={meta.appearance}
      dot
      variant={meta.variant}
      {...badgeProps}>
      {meta.label}
    </Badge>
  );
}
